<template>
    <!-- Until it is first used, only the trigger: a list row carries one of
         these per editable cell, and a closed b-dropdown still renders its menu,
         moves it into the body and listens on the document. -->
    <div
        v-if="!armed"
        :class="['dropdown', 'dropdown-menu-animation', resolvedPosition, placeholderClasses, $attrs.class]"
        :style="$attrs.style"
    >
        <div
            class="dropdown-trigger"
            :tabindex="isDisabled ? undefined : 0"
            aria-haspopup="true"
            @click="armAndOpen"
            @keydown.enter.space.prevent="armAndOpen"
        >
            <slot name="trigger" :active="false"></slot>
        </div>
    </div>
    <b-dropdown
        v-else
        ref="dropdown"
        v-bind="$attrs"
        append-to-body
        scrollable
        :max-height="menuMaxHeight"
        :can-close="['escape']"
        :position="resolvedPosition"
        @active-change="onActiveChange"
    >
        <template #trigger="{ active }">
            <slot name="trigger" :active="active"></slot>
        </template>
        <slot></slot>
    </b-dropdown>
</template>

<script>
// Module-scoped: the close() of whichever SmartDropdown instance is open.
let closeActiveDropdown = null;
</script>

<script setup>
import { ref, computed, nextTick, onBeforeUnmount, useAttrs } from 'vue';
import { opensOnFirstClick } from '@/utils/layoutReads';

/**
 * Drop-in replacement for <b-dropdown append-to-body> that keeps a menu inside
 * the window: it picks the open direction (top/bottom × left/right) from the
 * space available around the trigger, and caps the menu's height to that space
 * so a long menu scrolls inside itself instead of running off the screen.
 * Buefy honours the `position` prop verbatim and does no collision handling,
 * so both are computed on open.
 *
 * It also owns the outside-click close. Buefy's own test walks the children of
 * the menu it holds a ref to, which — once the menu has been moved into the
 * body — no longer contains what the user sees: clicking an item that redraws
 * the menu (unfolding a filter list) counted as a click outside and shut the
 * dropdown. Hence `can-close="['escape']"` and the listener below.
 *
 * Usage is identical to <b-dropdown>: pass the same attrs, the #trigger slot and
 * <b-dropdown-item>s as default slot. Do not pass `position` — it is managed.
 *
 * The b-dropdown itself is only mounted the first time the trigger is used.
 * Every editable cell of a list is one of these: mounted up front, each row
 * rendered its menu, moved it into the body and added document listeners, and
 * the list froze for a moment on every page it scrolled in. Before that first
 * use the trigger is rendered in the same markup, so nothing on screen moves.
 * A menu that opens without a click — hoverable, inline, other `triggers` — is
 * mounted at once.
 */
defineOptions({ inheritAttrs: false });

const props = defineProps({
    // Fallback used before the menu is measured and when space is ample.
    preferredPosition: {
        type: String,
        default: 'is-bottom-right'
    }
});

const attrs = useAttrs();

// A boolean attribute written bare (`hoverable`) arrives as an empty string.
function attr(name) {
    const camel = name.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    const value = attrs[name] ?? attrs[camel];

    return value === '' ? true : value;
}

const isDisabled = computed(() => Boolean(attr('disabled')));

const armed = ref(
    !opensOnFirstClick({ hoverable: attr('hoverable'), inline: attr('inline'), triggers: attr('triggers') })
);

const placeholderClasses = computed(() => ({
    'is-disabled': isDisabled.value,
    'is-mobile-modal': attr('mobile-modal') !== false,
    'is-expanded': Boolean(attr('expanded'))
}));

const dropdown = ref(null);
const resolvedPosition = ref(props.preferredPosition);
// Measured on open; Buefy turns it into the menu's max-height + overflow.
const menuMaxHeight = ref(null);

// Gap kept between the menu and the viewport edge (px)
const VIEWPORT_MARGIN = 8;
// Below this width Buefy renders a full-screen modal — positioning is moot.
const MOBILE_BREAKPOINT = 769;
// A menu squeezed against an edge still has to show something.
const MIN_MENU_HEIGHT = 180;

function close() {
    if (dropdown.value) {
        dropdown.value.isActive = false;
    }
}

/**
 * Open the menu without a press on its trigger.
 *
 * For a second route to the same control — a context-menu entry, a keyboard
 * shortcut — where the alternative is a parent carrying its own copy of what
 * the menu offers.
 */
function open() {
    if (!armed.value) {
        arm().then(() => open());
        return;
    }

    if (dropdown.value) {
        dropdown.value.isActive = true;
    }
}

function arm() {
    armed.value = true;

    return nextTick();
}

/**
 * The first use of the trigger: mount the real dropdown, then open it the way
 * its own trigger would have — `toggle()` honours `disabled` — and hand it the
 * focus the placeholder held, so a keyboard user carries on from the same spot.
 */
async function armAndOpen() {
    if (isDisabled.value) {
        return;
    }

    const hadFocus = document.activeElement?.classList?.contains('dropdown-trigger');

    await arm();
    dropdown.value?.toggle();

    if (hadFocus) {
        dropdown.value?.$refs?.trigger?.focus();
    }
}

// Let a parent drive the menu programmatically (e.g. closing it after
// committing an inline edit whose control lives in a `custom` item that never
// auto-closes, or opening it from somewhere other than its trigger).
defineExpose({ close, open });

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick);

    if (closeActiveDropdown === close) {
        closeActiveDropdown = null;
    }
});

/**
 * Close when the click lands neither on the trigger nor inside the menu the
 * user can actually see — the element Buefy moved into the body.
 */
function onDocumentClick(event) {
    const comp = dropdown.value;

    if (!comp?.isActive) {
        return;
    }

    const menu = comp.$refs?.dropdownMenu;
    const body = comp.$data?._bodyEl ?? comp._bodyEl;
    const trigger = comp.$refs?.trigger;

    // The event's path, not the element's ancestors *now*: an item that redraws
    // the menu when clicked (unfolding a filter list) is replaced by Vue before
    // this listener runs — asking whether the menu still contains it answers no,
    // and the menu would close on its own item. The path is recorded when the
    // click is dispatched, so it still names what was clicked.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const inside = path.some(node => node === menu || node === body || node === trigger);

    if (inside || menu?.contains(event.target) || body?.contains(event.target) || trigger?.contains(event.target)) {
        return;
    }

    close();
}

function onActiveChange(active) {
    // Buefy flips the menu to aria-hidden on close while the clicked item still
    // holds focus, which browsers block ("Blocked aria-hidden on an element
    // because its descendant retained focus"). Drop the focus first.
    if (!active) {
        const focused = document.activeElement;
        if (focused?.closest?.('.dropdown-menu')) {
            focused.blur();
        }
    }

    // Only one SmartDropdown open at a time. Triggers often sit inside cells
    // that stop click propagation (to avoid row-click navigation), which
    // defeats Buefy's document-level outside-click close — enforce it here.
    if (active) {
        if (closeActiveDropdown && closeActiveDropdown !== close) {
            closeActiveDropdown();
        }
        closeActiveDropdown = close;
    } else if (closeActiveDropdown === close) {
        closeActiveDropdown = null;
    }

    // Registered after the opening click has finished travelling, or it would
    // close the menu it just opened.
    if (active) {
        setTimeout(() => document.addEventListener('click', onDocumentClick), 0);
    } else {
        document.removeEventListener('click', onDocumentClick);
    }

    if (!active || window.innerWidth < MOBILE_BREAKPOINT) {
        return;
    }
    // Let the menu render, then measure and flip (double tick mirrors Buefy's
    // own append-to-body timing so our position wins).
    nextTick(() => nextTick(() => positionMenu()));
}

function positionMenu() {
    const comp = dropdown.value;
    const trigger = comp?.$refs?.trigger;
    const menu = comp?.$refs?.dropdownMenu;
    if (!trigger || !menu) {
        return;
    }

    const rect = trigger.getBoundingClientRect();
    const menuWidth = menu.offsetWidth || 200;
    const menuHeight = menu.offsetHeight || 200;
    const { innerWidth: vw, innerHeight: vh } = window;

    // Vertical: prefer opening downward; flip up only when it doesn't fit below
    // but fits better above.
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const vertical = spaceBelow < menuHeight + VIEWPORT_MARGIN && spaceAbove > spaceBelow ? 'top' : 'bottom';

    // Horizontal (Buefy semantics):
    //   is-*-right → menu's LEFT edge at the trigger's left, extends rightward
    //   is-*-left  → menu's RIGHT edge at the trigger's right, extends leftward
    const spaceRight = vw - rect.left;
    const spaceLeft = rect.right;
    const horizontal = spaceRight < menuWidth + VIEWPORT_MARGIN && spaceLeft > spaceRight ? 'left' : 'right';

    resolvedPosition.value = `is-${vertical}-${horizontal}`;

    // A menu taller than the space it opens into scrolls inside itself rather
    // than running off the screen — a filter menu listing every column of a list
    // is easily taller than the viewport, and its last entries were unreachable.
    menuMaxHeight.value = Math.max(
        MIN_MENU_HEIGHT,
        (vertical === 'bottom' ? spaceBelow : spaceAbove) - VIEWPORT_MARGIN * 2
    );

    // Re-run Buefy's body positioning once the new position prop has propagated.
    nextTick(() => comp.updateAppendToBody?.());
}
</script>
